import api from '@/shared/utils/apiConfig';
import type { TreatmentCatalogActorRef } from '@/modules/treatment-catalog/api/treatmentCategory.api';

export const JOURNAL_NAME_MAX_LENGTH = 100;

export const JOURNAL_COUNT_MODES = ['none', 'visit', 'duration'] as const;

export type JournalCountMode = (typeof JOURNAL_COUNT_MODES)[number];

export const DURATION_MINUTES_MAX = 1440;

export const resolveJournalCountMode = (item?: {
  countMode?: JournalCountMode | null;
  countsTowardKpi?: boolean | null;
} | null): JournalCountMode => {
  if (item?.countMode === 'none' || item?.countMode === 'visit' || item?.countMode === 'duration') {
    return item.countMode;
  }
  return item?.countsTowardKpi === false ? 'none' : 'visit';
};

export const sanitizeDurationMinutesInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (!digits) return '';
  const value = Number(digits);
  if (value > DURATION_MINUTES_MAX) return String(DURATION_MINUTES_MAX);
  return String(value);
};

export interface TreatmentJournal {
  id: string;
  name: string;
  countMode?: JournalCountMode;
  countsTowardKpi: boolean;
  usesAssistantRings?: boolean;
  isActive: boolean;
  sortOrder: number;
  createdBy?: TreatmentCatalogActorRef;
  updatedBy?: TreatmentCatalogActorRef;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateTreatmentJournalPayload {
  name: string;
  countMode?: JournalCountMode;
  countsTowardKpi?: boolean;
  usesAssistantRings?: boolean;
  sortOrder?: number;
}

export interface UpdateTreatmentJournalPayload {
  name?: string;
  countMode?: JournalCountMode;
  countsTowardKpi?: boolean;
  usesAssistantRings?: boolean;
  sortOrder?: number;
  isActive?: boolean;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  count?: number;
  message?: string;
}

export const treatmentJournalAPI = {
  getAll: async (includeInactive = false): Promise<ApiResponse<TreatmentJournal[]>> => {
    const response = await api.get('/treatment-journals', {
      params: includeInactive ? { includeInactive: 'true' } : undefined,
    });
    return response.data;
  },

  create: async (payload: CreateTreatmentJournalPayload): Promise<ApiResponse<TreatmentJournal>> => {
    const response = await api.post('/treatment-journals', payload);
    return response.data;
  },

  update: async (
    id: string,
    payload: UpdateTreatmentJournalPayload
  ): Promise<ApiResponse<TreatmentJournal>> => {
    const response = await api.put(`/treatment-journals/${id}`, payload);
    return response.data;
  },

  deactivate: async (id: string): Promise<ApiResponse<TreatmentJournal>> => {
    const response = await api.delete(`/treatment-journals/${id}`);
    return response.data;
  },
};
